// useBoxRegroupDraft — staged-change state for the logist regroup
// edit mode (#1178 §7 step 5). Holds the per-item ordinal overrides
// the logist has selected but not yet saved, exposes a `dirty` flag
// the caller feeds into `useUnsavedChangesGuard`, and a `batch` array
// of `BoxItemRegroupInput` rows ready for the PATCH body.
//
// The hook is pure stage state — it does NOT issue HTTP, does NOT
// debounce, does NOT mutate React Query cache. That responsibility
// stays in the mutation wrapper (#1178 §7 step 6) so this hook is
// trivially testable on a synchronous render.

import { useCallback, useMemo, useState } from 'react';
import type { BoxItemRegroupInput } from '@magimp/shared';
import { REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX } from '@magimp/shared';

export interface RegroupBaselineItem {
  itemId: string;
  groupOrdinal: number;
}

export interface UseBoxRegroupDraftOpts {
  /**
   * Baseline ordinals as observed on the most recent server fetch.
   * When the parent refetches (post-save, post-realtime), pass the
   * new array — the hook re-derives `dirty` against the new baseline
   * but does NOT auto-discard pending stage (caller decides via
   * `reset()`).
   */
  baseline: ReadonlyArray<RegroupBaselineItem>;
}

export interface UseBoxRegroupDraftResult {
  /** Effective ordinal: staged value if set, otherwise baseline. */
  effective: (itemId: string) => number;
  /** Stage a new ordinal for an item. */
  stage: (itemId: string, ordinal: number) => void;
  /** True when any staged value differs from baseline. */
  dirty: boolean;
  /** Discard all staged changes; idempotent if already clean. */
  reset: () => void;
  /**
   * Build the PATCH body — emits ONLY items whose staged value
   * differs from baseline (no-op items are filtered out so the wire
   * batch matches D16 "every requested groupOrdinal already matches DB
   * = no-op" semantics). Returns `[]` when clean.
   */
  batch: ReadonlyArray<BoxItemRegroupInput>;
  /**
   * Next ordinal to mint for a "+ New group" action: `max(effective) +
   * 1`, capped at `REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX + 1`. The
   * dropdown disables the new-group entry when this exceeds the cap.
   */
  nextNewGroupOrdinal: number;
  /** Existing ordinals among effective values (deduped ascending). */
  existingGroupOrdinals: ReadonlyArray<number>;
}

export function useBoxRegroupDraft(opts: UseBoxRegroupDraftOpts): UseBoxRegroupDraftResult {
  const { baseline } = opts;
  const [stage, setStage] = useState<ReadonlyMap<string, number>>(() => new Map());

  const baselineById = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of baseline) m.set(b.itemId, b.groupOrdinal);
    return m;
  }, [baseline]);

  const effective = useCallback(
    (itemId: string): number => {
      const staged = stage.get(itemId);
      if (staged !== undefined) return staged;
      return baselineById.get(itemId) ?? 0;
    },
    [stage, baselineById],
  );

  const stageOrdinal = useCallback(
    (itemId: string, ordinal: number): void => {
      // INVARIANT: clamp to wire bounds so a buggy caller cannot stage
      //   a value that would 400 at PATCH time. The dropdown gates the
      //   upper bound at the option level; this is defence in depth.
      if (!Number.isInteger(ordinal) || ordinal < 0) return;
      if (ordinal > REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX) return;
      setStage((prev) => {
        const next = new Map(prev);
        const base = baselineById.get(itemId);
        // If the new ordinal equals baseline, drop the entry so
        //   `dirty` and `batch` stay tight — re-selecting the original
        //   value should NOT count as dirty.
        if (base !== undefined && base === ordinal) {
          next.delete(itemId);
        } else {
          next.set(itemId, ordinal);
        }
        return next;
      });
    },
    [baselineById],
  );

  const reset = useCallback((): void => {
    setStage((prev) => (prev.size === 0 ? prev : new Map()));
  }, []);

  const dirty = stage.size > 0;

  const batch = useMemo<ReadonlyArray<BoxItemRegroupInput>>(() => {
    const out: BoxItemRegroupInput[] = [];
    for (const [itemId, ordinal] of stage) {
      out.push({ itemId, groupOrdinal: ordinal });
    }
    return out;
  }, [stage]);

  const existingGroupOrdinals = useMemo<ReadonlyArray<number>>(() => {
    const set = new Set<number>();
    for (const b of baseline) {
      const staged = stage.get(b.itemId);
      set.add(staged ?? b.groupOrdinal);
    }
    return [...set].sort((a, b) => a - b);
  }, [baseline, stage]);

  const nextNewGroupOrdinal = useMemo<number>(() => {
    if (existingGroupOrdinals.length === 0) return 0;
    return existingGroupOrdinals[existingGroupOrdinals.length - 1]! + 1;
  }, [existingGroupOrdinals]);

  return {
    effective,
    stage: stageOrdinal,
    dirty,
    reset,
    batch,
    nextNewGroupOrdinal,
    existingGroupOrdinals,
  };
}
